import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { 
  Building2, 
  ArrowLeft, 
  Plus, 
  Search,
  Package,
  Truck,
  CheckCircle,
  Clock,
  ShoppingCart,
  Filter,
  Bookmark,
  Trash2,
  MoreVertical,
  PackageCheck,
  X,
  Copy,
  Loader2,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Send,
  HelpCircle,
  MessageSquare,
  StickyNote,
  Zap,
  CheckSquare,
  Square
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import QuickOrderDialog from "@/components/purchasing/QuickOrderDialog";
import SpecialOrderDialog from "@/components/purchasing/SpecialOrderDialog";
import PurchasingLookup from "@/components/purchasing/PurchasingLookup";
import { dualPriceText, convertToSEK } from "@/components/DualPrice";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrdersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [suppliers, setSuppliers] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  
  // Read search param from URL on mount
  useEffect(() => {
    const searchFromUrl = searchParams.get("search");
    if (searchFromUrl) {
      setSearchQuery(searchFromUrl);
      // Clear the URL param after reading
      setSearchParams({});
    }
  }, []);
  
  // Reservation dialog
  const [showReservationDialog, setShowReservationDialog] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [convertPrompt, setConvertPrompt] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [reservationForm, setReservationForm] = useState({
    customer_order_number: "",
    quantity: 1
  });
  const [saving, setSaving] = useState(false);
  
  // Edit week dialog
  const [showEditWeekDialog, setShowEditWeekDialog] = useState(false);
  const [editWeekProduct, setEditWeekProduct] = useState(null);
  const [editWeekValue, setEditWeekValue] = useState("");
  
  // Edit quantity dialog
  const [showEditQuantityDialog, setShowEditQuantityDialog] = useState(false);
  const [editQuantityProduct, setEditQuantityProduct] = useState(null);
  const [editQuantityValue, setEditQuantityValue] = useState(1);
  
  // Note dialog
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [noteProduct, setNoteProduct] = useState(null);
  const [noteValue, setNoteValue] = useState("");
  
  // Receive delivery dialog
  const [showReceiveDialog, setShowReceiveDialog] = useState(false);
  const [receiveSupplier, setReceiveSupplier] = useState("");
  const [receiveProducts, setReceiveProducts] = useState([]);
  const [selectedForReceive, setSelectedForReceive] = useState({});
  const [receiveStatusFilter, setReceiveStatusFilter] = useState("all"); // "all", "ordered", "in_transit"
  
  // --- Inquiry (förfrågan) workflow state ---
  const [showInquiryDialog, setShowInquiryDialog] = useState(false);
  const [inquiryProduct, setInquiryProduct] = useState(null);
  const [inquirySelected, setInquirySelected] = useState([]);
  const [inquiryPricedIds, setInquiryPricedIds] = useState([]);
  const [inquiryPriceMap, setInquiryPriceMap] = useState({});
  const [inquiryUnavailableIds, setInquiryUnavailableIds] = useState([]);
  const [showResponseDialog, setShowResponseDialog] = useState(false);
  const [responseProduct, setResponseProduct] = useState(null);
  const [responsePriceMap, setResponsePriceMap] = useState({});
  const [responseForm, setResponseForm] = useState({ supplier_id: "", available: true, price: "", currency: "SEK", chemical_tax: "", note: "" });
  const [showChooseDialog, setShowChooseDialog] = useState(false);
  const [showSpecialOrder, setShowSpecialOrder] = useState(false);
  const [inquirySelectedSupplier, setInquirySelectedSupplier] = useState("");
  const [inquiryCopied, setInquiryCopied] = useState(null);
  const [inquiryCopyKeys, setInquiryCopyKeys] = useState([]);
  const [showSentCaseDialog, setShowSentCaseDialog] = useState(false);
  const [sentCaseGroup, setSentCaseGroup] = useState(null);
  const [sentCaseNumber, setSentCaseNumber] = useState("");
  const [inquirySubFilter, setInquirySubFilter] = useState("all");
  const [chooseProduct, setChooseProduct] = useState(null);
  const [chooseForm, setChooseForm] = useState({ supplier_id: "", unit_price: "", currency: "SEK", chemical_tax: "" });
  
  // Quick order dialog - using shared component
  const [showQuickOrderDialog, setShowQuickOrderDialog] = useState(false);
  const [quickOrderPreselect, setQuickOrderPreselect] = useState(null);
  
  // Exchange rate for EUR->SEK
  const [exchangeRate, setExchangeRate] = useState(11.5); // Default fallback
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 50;

  // Calculate current ISO week number
  const getCurrentWeek = () => {
    const now = new Date();
    const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };
  const currentWeek = getCurrentWeek();

  useEffect(() => {
    fetchData();
    
    // Check for quickOrder query param (from "Beställ mer" button)
    const quickOrder = searchParams.get('quickOrder');
    const eanParam = searchParams.get('ean');
    const artParam = searchParams.get('artikelnummer');
    if (quickOrder === 'true' && eanParam) {
      // Wait for data to load before opening quick order dialog
      const waitForData = setInterval(() => {
        // suppliers is set in state after fetchData completes
        if (suppliers.length > 0 || !loading) {
          clearInterval(waitForData);
          setQuickOrderPreselect({
            ean: eanParam,
            artikelnummer: artParam || ''
          });
          setShowQuickOrderDialog(true);
          setSearchParams({});
        }
      }, 200);
      // Cleanup after 10s max
      setTimeout(() => clearInterval(waitForData), 10000);
    }
    
    // Check for return flow from product creation or price addition
    const returnData = sessionStorage.getItem('returnToOrder');
    if (returnData) {
      try {
        const data = JSON.parse(returnData);
        sessionStorage.removeItem('returnToOrder');
        if (data.fromQuickOrder) {
          const waitForData2 = setInterval(() => {
            if (suppliers.length > 0 || !loading) {
              clearInterval(waitForData2);
              setQuickOrderPreselect({
                artikelnummer: data.artikelnummer,
                ean: data.ean
              });
              setShowQuickOrderDialog(true);
            }
          }, 200);
          setTimeout(() => clearInterval(waitForData2), 10000);
        }
      } catch (e) {
        console.error("Error parsing return data:", e);
      }
    }
  }, []);

  const fetchData = async () => {
    try {
      // Fetch ALL data in parallel for maximum speed
      // Use pending_only=true to only fetch orders with items that haven't been fully received
      const [suppliersRes, ordersRes, reservationsRes, rateRes] = await Promise.all([
        axios.get(`${API}/purchasing/suppliers`),
        axios.get(`${API}/purchasing/orders?pending_only=true&limit=2000`),
        axios.get(`${API}/purchasing/reservations`).catch(() => ({ data: [] })),
        axios.get(`${API}/purchasing/exchange-rate`).catch(() => ({ data: { rate: 11.5 } }))
      ]);
      
      setSuppliers(suppliersRes.data);
      setExchangeRate(rateRes.data?.rate || 11.5);
      
      // Handle both old array format and new paginated format
      const ordersData = ordersRes.data.orders || ordersRes.data;
      const allReservations = reservationsRes.data || [];
      
      // Build a flat list of all products from all orders
      const products = [];
      
      // Collect all order items
      const orderItems = [];
      for (const order of ordersData) {
        if (order.status === 'cancelled') continue;
        for (const item of order.items || []) {
          const receivedQty = item.received_quantity || 0;
          const pendingQty = item.quantity - receivedQty;
          if (pendingQty > 0) {
            orderItems.push({ order, item, pendingQty, receivedQty });
          }
        }
      }
      
      // Build reservation map for quick lookup
      const reservationMap = {};
      for (const r of allReservations) {
        const key = `${r.purchase_order_id}-${r.ean}`;
        if (!reservationMap[key]) {
          reservationMap[key] = [];
        }
        reservationMap[key].push(r);
      }
      
      // Process all items with cached data
      for (const { order, item, pendingQty, receivedQty } of orderItems) {
        const supplier = suppliersRes.data.find(s => s.id === order.supplier_id);
        
        const key = `${order.id}-${item.ean}`;
        const reservations = reservationMap[key] || [];
        const reservedQty = reservations.reduce((sum, r) => sum + r.quantity, 0);
        
        products.push({
          ean: item.ean,
          artikelnummer: item.artikelnummer,
          produktnamn: item.produktnamn,
          quantity: item.quantity,
          received: receivedQty,
          pending: pendingQty,
          reserved: reservedQty,
          unreserved: pendingQty - reservedQty,
          reservations: reservations,
          order_id: order.id,
          order_number: order.order_number,
          order_status: order.status,
          expected_week: order.expected_delivery_week,
          supplier_id: order.supplier_id,
          supplier_name: supplier?.company_name || order.supplier_name || "",
          created_at: order.created_at,
          status_updated_at: order.status_updated_at || order.created_at,
          unit_price: item.unit_price || 0,
          currency: item.currency || 'SEK',
          note: item.note || '',
          inquiry_suppliers: order.inquiry_suppliers || [],
          inquiry_sent_suppliers: order.inquiry_sent_suppliers || [],
          inquiry_case_numbers: order.inquiry_case_numbers || {},
          inquiry_responses: order.inquiry_responses || []
        });
      }
      
      // Sort by created date (newest first)
      products.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      setAllProducts(products);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Kunde inte hämta data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="text-red-500 border-red-500"><ShoppingCart className="h-3 w-3 mr-1" />Ska beställas</Badge>;
      case 'ready_for_inquiry':
        return <Badge variant="outline" className="text-indigo-500 border-indigo-500"><HelpCircle className="h-3 w-3 mr-1" />Redo för förfrågan</Badge>;
      case 'inquiry_sent':
        return <Badge variant="outline" className="text-cyan-600 border-cyan-600"><Send className="h-3 w-3 mr-1" />Förfrågan skickad</Badge>;
      case 'uncertain':
        return <Badge variant="outline" className="text-purple-500 border-purple-500"><HelpCircle className="h-3 w-3 mr-1" />Osäker</Badge>;
      case 'ordered':
        return <Badge variant="outline" className="text-blue-500 border-blue-500"><Clock className="h-3 w-3 mr-1" />Beställd</Badge>;
      case 'in_transit':
        return <Badge variant="outline" className="text-emerald-500 border-emerald-500"><Truck className="h-3 w-3 mr-1" />På väg</Badge>;
      case 'received':
        return <Badge variant="outline" className="text-green-500 border-green-500"><CheckCircle className="h-3 w-3 mr-1" />Mottagen</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Short status label + color helpers for the quantity block
  const statusText = (status) => ({
    pending: 'Ska beställas', ready_for_inquiry: 'Redo för förfrågan', inquiry_sent: 'Förfrågan skickad',
    uncertain: 'Osäker', in_transit: 'På väg', ordered: 'Beställd'
  }[status] || status);
  const statusColor = (status) => ({
    pending: 'text-red-500', ready_for_inquiry: 'text-indigo-500', inquiry_sent: 'text-cyan-600',
    uncertain: 'text-purple-500', in_transit: 'text-emerald-500', ordered: 'text-blue-500'
  }[status] || 'text-blue-500');

  const formatRelativeTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "just nu";
    if (diffMins < 60) return `${diffMins} min sedan`;
    if (diffHours < 24) return `${diffHours} tim sedan`;
    if (diffDays < 7) return `${diffDays} dag${diffDays > 1 ? 'ar' : ''} sedan`;
    
    return date.toLocaleDateString("sv-SE", { day: "numeric", month: "short" });
  };

  const openReservationDialog = (product) => {
    setSelectedProduct(product);
    setReservationForm({
      customer_order_number: "",
      quantity: Math.min(1, product.unreserved)
    });
    setShowReservationDialog(true);
  };

  const handleAddReservation = async () => {
    if (!reservationForm.customer_order_number || reservationForm.quantity < 1) {
      toast.error("Fyll i kundordernummer och antal");
      return;
    }
    
    if (reservationForm.quantity > selectedProduct.unreserved) {
      toast.error(`Kan inte reservera mer än ${selectedProduct.unreserved} st`);
      return;
    }
    
    setSaving(true);
    try {
      const res = await axios.post(`${API}/purchasing/reservations`, {
        ean: selectedProduct.ean,
        customer_order_number: reservationForm.customer_order_number,
        quantity: reservationForm.quantity,
        purchase_order_id: selectedProduct.order_id
      });

      setShowReservationDialog(false);
      if (res.data?.stock_available) {
        // Product is in stock – ask the user whether to turn the reservation into a booking
        setConvertPrompt({
          reservationId: res.data.id,
          quantity: res.data.quantity || reservationForm.quantity,
          customerOrderNumber: reservationForm.customer_order_number,
          artikelnummer: selectedProduct.artikelnummer,
          available: res.data.available_quantity,
        });
        setShowConvertDialog(true);
      } else {
        toast.success(`${reservationForm.quantity} st reserverade för ${reservationForm.customer_order_number}`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte skapa reservation");
    } finally {
      setSaving(false);
    }
  };

  const handleConvertReservation = async () => {
    if (!convertPrompt) return;
    setSaving(true);
    try {
      await axios.post(`${API}/purchasing/reservations/${convertPrompt.reservationId}/convert-to-booking`);
      toast.success(`Reservationen omvandlad till bokning för ${convertPrompt.customerOrderNumber}`);
      setShowConvertDialog(false);
      setConvertPrompt(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte omvandla till bokning");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteReservation = async (reservationId) => {
    try {
      await axios.delete(`${API}/purchasing/reservations/${reservationId}`);
      toast.success("Reservation borttagen");
      fetchData();
    } catch (error) {
      toast.error("Kunde inte ta bort reservation");
    }
  };

  const handleRemoveProduct = async (product) => {
    if (!confirm(`Ta bort ${product.artikelnummer} från order ${product.order_number}?`)) return;
    
    try {
      const response = await axios.delete(`${API}/purchasing/orders/${product.order_id}/items/${product.ean}`);
      if (response.data.already_gone) {
        toast.success("Spökrad rensad - listan uppdateras");
      } else if (response.data.order_deleted) {
        toast.success(`Order ${product.order_number} borttagen (inga produkter kvar)`);
      } else {
        toast.success(`${product.artikelnummer} borttagen från ordern`);
      }
      fetchData();
    } catch (error) {
      // Always refresh to clear any stale rows, even on error
      toast.error(error.response?.data?.detail || "Kunde inte ta bort produkt");
      fetchData();
    }
  };

  const handleChangeOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/purchasing/orders/${orderId}`, { status: newStatus });
      toast.success("Status ändrad");
      fetchData();
    } catch (error) {
      toast.error("Kunde inte ändra status");
    }
  };

  const supplierName = (id) => suppliers.find(s => s.id === id)?.company_name || "Okänd";
  const supplierCurrency = (id) => suppliers.find(s => s.id === id)?.default_currency || "SEK";

  // Inquiry sub-state for a line: 'ready' (none sent), 'partial' (some sent), 'sent' (all sent)
  const inquiryState = (p) => {
    const chosen = (p.inquiry_suppliers || []).length;
    const sent = (p.inquiry_sent_suppliers || []).length;
    if (sent === 0) return 'ready';
    if (sent < chosen) return 'partial';
    return 'sent';
  };

  // Inquiry supplier selection
  const openInquiryDialog = async (product) => {
    setInquiryProduct(product);
    setInquirySelected(product.inquiry_suppliers || []);
    setInquiryPricedIds([]);
    setInquiryPriceMap({});
    setInquiryUnavailableIds([]);
    setShowInquiryDialog(true);
    // Sync price + unavailable status from the prislista for this product
    try {
      const res = await axios.get(`${API}/purchasing/prices/by-ean/${product.ean}`);
      const all = res.data?.prices || [];
      const priced = all.filter(p => (p.purchase_price || 0) > 0 && !p.unavailable);
      const map = {};
      priced.forEach(p => { map[p.supplier_id] = { price: p.purchase_price, currency: p.currency || "SEK" }; });
      setInquiryPriceMap(map);
      setInquiryPricedIds([...new Set(priced.map(p => p.supplier_id))]);
      setInquiryUnavailableIds([...new Set(all.filter(p => p.unavailable).map(p => p.supplier_id))]);
    } catch { /* ignore */ }
  };
  // How often we've used/checked each supplier (across current orders) – for sorting
  const inquiryUsageMap = () => {
    const m = {};
    for (const p of allProducts) {
      for (const sid of (p.inquiry_suppliers || [])) m[sid] = (m[sid] || 0) + 1;
      if (p.supplier_id) m[p.supplier_id] = (m[p.supplier_id] || 0) + 1;
    }
    return m;
  };
  const toggleInquirySupplier = (id) => {
    setInquirySelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const handleSaveInquirySuppliers = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/purchasing/orders/${inquiryProduct.order_id}/inquiry-suppliers`, { supplier_ids: inquirySelected });
      toast.success(inquirySelected.length ? `${inquirySelected.length} leverantör(er) valda för förfrågan` : "Förfrågan-leverantörer rensade");
      setShowInquiryDialog(false);
      fetchData();
    } catch (e) { toast.error("Kunde inte spara"); } finally { setSaving(false); }
  };

  // Build response-form values for a supplier. Prefills an existing recorded response,
  // else falls back to the supplier's current prislista price (priceMap).
  const responseFormForSupplier = (product, sid, productTax, priceMap = {}) => {
    const existing = (product.inquiry_responses || []).find(r => r.supplier_id === sid);
    if (existing) {
      return {
        supplier_id: sid,
        available: existing.available !== false,
        price: existing.price != null ? String(existing.price) : "",
        currency: existing.currency || "SEK",
        chemical_tax: existing.chemical_tax != null && existing.chemical_tax !== ""
          ? String(existing.chemical_tax)
          : (productTax ? String(productTax) : ""),
        note: existing.note || ""
      };
    }
    const listed = priceMap[sid];
    return {
      supplier_id: sid,
      available: true,
      price: listed?.price != null ? String(listed.price) : "",
      currency: listed?.currency || supplierCurrency(sid),
      chemical_tax: (listed?.chemical_tax || productTax) ? String(listed?.chemical_tax || productTax) : "",
      note: ""
    };
  };

  // Register a supplier response
  const openResponseDialog = async (product, supplierId) => {
    setResponseProduct(product);
    setResponsePriceMap({});
    const sid = supplierId || (product.inquiry_suppliers?.[0] || "");
    setResponseForm(responseFormForSupplier(product, sid, 0, {}));
    setShowResponseDialog(true);
    // Fetch prislista prices + product-level chemical tax, then prefill fallbacks
    try {
      const [pricesRes, taxRes] = await Promise.all([
        axios.get(`${API}/purchasing/prices/by-ean/${product.ean}`),
        axios.get(`${API}/purchasing/product-chemical-tax/${product.ean}`).catch(() => ({ data: {} }))
      ]);
      const priced = (pricesRes.data?.prices || []).filter(p => (p.purchase_price || 0) > 0 && !p.unavailable);
      const map = {};
      priced.forEach(p => { map[p.supplier_id] = { price: p.purchase_price, currency: p.currency || "SEK", chemical_tax: p.chemical_tax || 0 }; });
      setResponsePriceMap(map);
      const tax = taxRes.data?.chemical_tax || 0;
      setResponseForm(prev => {
        const hasResp = (product.inquiry_responses || []).some(r => r.supplier_id === prev.supplier_id);
        if (hasResp) return prev.chemical_tax ? prev : { ...prev, chemical_tax: tax ? String(tax) : prev.chemical_tax };
        const listed = map[prev.supplier_id];
        return {
          ...prev,
          price: prev.price || (listed?.price != null ? String(listed.price) : ""),
          currency: listed?.currency || prev.currency,
          chemical_tax: prev.chemical_tax || (listed?.chemical_tax ? String(listed.chemical_tax) : (tax ? String(tax) : ""))
        };
      });
    } catch { /* ignore */ }
  };
  const handleSaveResponse = async () => {
    if (!responseForm.supplier_id) { toast.error("Välj leverantör"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/purchasing/orders/${responseProduct.order_id}/inquiry-response`, {
        supplier_id: responseForm.supplier_id,
        available: responseForm.available,
        price: responseForm.available === true && responseForm.price ? parseFloat(responseForm.price) : null,
        currency: responseForm.currency,
        chemical_tax: responseForm.chemical_tax ? parseFloat(responseForm.chemical_tax) : null,
        note: responseForm.note
      });
      toast.success(
        responseForm.available === true ? "Svar registrerat"
          : responseForm.available === false ? `${supplierName(responseForm.supplier_id)} markerad som otillgänglig`
            : `${supplierName(responseForm.supplier_id)} markerad som "inget svar"`
      );
      setShowResponseDialog(false);
      fetchData();
    } catch (e) { toast.error("Kunde inte spara svar"); } finally { setSaving(false); }
  };

  // Choose final supplier + actual price
  const openChooseDialog = async (product) => {
    setChooseProduct(product);
    const avail = (product.inquiry_responses || []).filter(r => r.available && r.price);
    avail.sort((a, b) => (a.price || 0) - (b.price || 0));
    const best = avail[0];
    setChooseForm({
      supplier_id: best?.supplier_id || product.inquiry_suppliers?.[0] || "",
      unit_price: best?.price ? String(best.price) : "",
      currency: best?.currency || "SEK",
      chemical_tax: best?.chemical_tax != null && best?.chemical_tax !== "" ? String(best.chemical_tax) : "",
      expected_week: (product.expected_week && product.expected_week >= currentWeek) ? String(product.expected_week) : String(currentWeek)
    });
    setShowChooseDialog(true);
    // Chemical tax is product-level: prefill from prislista if the chosen response has none
    try {
      const res = await axios.get(`${API}/purchasing/product-chemical-tax/${product.ean}`);
      const tax = res.data?.chemical_tax || 0;
      if (tax) {
        setChooseForm(prev => ({ ...prev, chemical_tax: prev.chemical_tax ? prev.chemical_tax : String(tax) }));
      }
    } catch { /* ignore */ }
  };
  const handleChooseSupplier = async () => {
    if (!chooseForm.supplier_id) { toast.error("Välj leverantör"); return; }
    if (!chooseForm.unit_price || parseFloat(chooseForm.unit_price) <= 0) { toast.error("Ange pris"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/purchasing/orders/${chooseProduct.order_id}/choose-supplier`, {
        supplier_id: chooseForm.supplier_id,
        unit_price: parseFloat(chooseForm.unit_price),
        currency: chooseForm.currency,
        chemical_tax: chooseForm.chemical_tax ? parseFloat(chooseForm.chemical_tax) : null,
        expected_delivery_week: chooseForm.expected_week ? parseInt(chooseForm.expected_week) : null
      });
      toast.success(`Beställs från ${supplierName(chooseForm.supplier_id)}`);
      setShowChooseDialog(false);
      fetchData();
    } catch (e) { toast.error("Kunde inte spara"); } finally { setSaving(false); }
  };

  const inquiryLineKey = (p) => `${p.order_id}::${p.ean}`;

  const handleCopyInquiry = async (group) => {
    const lines = group.lines.filter(p => inquiryCopyKeys.includes(inquiryLineKey(p)));
    if (lines.length === 0) { toast.error("Markera minst en produkt att skicka förfrågan på"); return; }
    const text = lines.map(p => `${p.pending}x ${p.artikelnummer} ${p.ean}`).join('\n');
    try { await navigator.clipboard.writeText(text); } catch { /* clipboard unavailable */ }
    toast.success(`${lines.length} rader kopierade – klistra in i mejlet till ${supplierName(group.supplier_id)}`);
    setInquiryCopied(group.supplier_id);
  };

  const handleCopyPriced = async (group) => {
    const lines = group.lines.filter(p => inquiryCopyKeys.includes(inquiryLineKey(p)));
    if (lines.length === 0) { toast.error("Markera minst en produkt att beställa"); return; }
    const text = lines.map(p => {
      const c = cheapestResponse(p);
      const priceStr = c ? ` – ${Math.round(c.price)} ${c.currency || 'SEK'}` : '';
      return `${p.pending}x ${p.artikelnummer} ${p.ean}${priceStr}`;
    }).join('\n');
    try { await navigator.clipboard.writeText(text); } catch { /* clipboard unavailable */ }
    toast.success(`${lines.length} rader kopierade – klistra in i mejlet till ${supplierName(group.supplier_id)}`);
    setInquiryCopied(group.supplier_id);
  };

  const handleConfirmSent = (group) => {
    const lines = group.lines.filter(p => inquiryCopyKeys.includes(inquiryLineKey(p)));
    const orderIds = [...new Set(lines.map(p => p.order_id))];
    if (orderIds.length === 0) { toast.error("Inga produkter markerade"); return; }
    // Open the optional ärendenummer dialog before marking as sent
    setSentCaseGroup({ supplier_id: group.supplier_id, order_ids: orderIds });
    setSentCaseNumber("");
    setShowSentCaseDialog(true);
  };

  const doConfirmSent = async (caseOverride) => {
    const g = sentCaseGroup;
    if (!g) return;
    const caseNo = (caseOverride !== undefined ? caseOverride : sentCaseNumber).trim();
    try {
      await axios.post(`${API}/purchasing/orders/inquiry/mark-sent`, {
        supplier_id: g.supplier_id,
        order_ids: g.order_ids,
        case_number: caseNo || null,
      });
      toast.success(`Förfrågan skickad till ${supplierName(g.supplier_id)}${caseNo ? ` (ärende ${caseNo})` : ''}`);
      setShowSentCaseDialog(false);
      setSentCaseGroup(null);
      setSentCaseNumber("");
      setInquiryCopied(null);
      setInquirySelectedSupplier("");
      setInquiryCopyKeys([]);
      fetchData();
    } catch (e) { toast.error("Kunde inte markera som skickad"); }
  };

  const openEditWeekDialog = (product) => {
    setEditWeekProduct(product);
    // If week has passed, default to current week
    const week = product.expected_week;
    setEditWeekValue(week && week >= currentWeek ? week.toString() : currentWeek.toString());
    setShowEditWeekDialog(true);
  };

  const handleUpdateExpectedWeek = async () => {
    if (!editWeekProduct) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/purchasing/orders/${editWeekProduct.order_id}`, {
        expected_delivery_week: editWeekValue ? parseInt(editWeekValue) : null
      });
      toast.success("Förväntad leveransvecka uppdaterad");
      setShowEditWeekDialog(false);
      fetchData();
    } catch (error) {
      toast.error("Kunde inte uppdatera");
    } finally {
      setSaving(false);
    }
  };

  // Edit quantity handlers
  const openEditQuantityDialog = (product) => {
    setEditQuantityProduct(product);
    setEditQuantityValue(product.quantity);
    setShowEditQuantityDialog(true);
  };

  const handleUpdateQuantity = async () => {
    if (!editQuantityProduct) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/purchasing/orders/${editQuantityProduct.order_id}/items/${editQuantityProduct.ean}`, {
        quantity: editQuantityValue
      });
      toast.success("Antal uppdaterat");
      setShowEditQuantityDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte uppdatera antal");
    } finally {
      setSaving(false);
    }
  };

  // Note handlers
  const openNoteDialog = (product) => {
    setNoteProduct(product);
    setNoteValue(product.note || "");
    setShowNoteDialog(true);
  };

  const handleSaveNote = async () => {
    if (!noteProduct) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/purchasing/orders/${noteProduct.order_id}/items/${noteProduct.ean}`, {
        note: noteValue
      });
      toast.success(noteValue ? "Notering sparad" : "Notering borttagen");
      setShowNoteDialog(false);
      fetchData();
    } catch (error) {
      toast.error("Kunde inte spara notering");
    } finally {
      setSaving(false);
    }
  };

  const openReceiveDialog = () => {
    setReceiveSupplier("");
    setReceiveProducts([]);
    setSelectedForReceive({});
    setReceiveStatusFilter("all");
    setShowReceiveDialog(true);
  };

  const handleSupplierSelect = (supplierId) => {
    setReceiveSupplier(supplierId);
    // Get products from this supplier - ONLY "ordered" and "in_transit" status
    const supplierProducts = allProducts.filter(p => 
      p.supplier_id === supplierId && 
      (p.order_status === "ordered" || p.order_status === "in_transit")
    );
    setReceiveProducts(supplierProducts);
    // Pre-select all products
    const selected = {};
    supplierProducts.forEach(p => {
      selected[`${p.order_id}-${p.ean}`] = true;
    });
    setSelectedForReceive(selected);
    setReceiveStatusFilter("all");
  };
  
  // Filter receive products based on status filter
  const filteredReceiveProducts = receiveProducts.filter(p => {
    if (receiveStatusFilter === "all") return true;
    return p.order_status === receiveStatusFilter;
  });

  // Update selection when filter changes - only select filtered items
  const handleFilterChange = (newFilter) => {
    setReceiveStatusFilter(newFilter);
    
    // Update selections to only include items matching the new filter
    const newSelected = {};
    receiveProducts.forEach(p => {
      const key = `${p.order_id}-${p.ean}`;
      const matchesFilter = newFilter === "all" || p.order_status === newFilter;
      // Only select if it matches the filter
      if (matchesFilter) {
        newSelected[key] = true;
      }
    });
    setSelectedForReceive(newSelected);
  };

  const toggleProductSelection = (product) => {
    const key = `${product.order_id}-${product.ean}`;
    setSelectedForReceive(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleProceedToReceive = () => {
    if (!receiveSupplier) {
      toast.error("Välj en leverantör");
      return;
    }
    
    if (receiveProducts.length === 0) {
      toast.error("Inga produkter att ta emot från denna leverantör");
      return;
    }
    
    // Filter by what's actually selected (only from visible/filtered products)
    const selectedProducts = receiveProducts.filter(p => {
      const key = `${p.order_id}-${p.ean}`;
      return selectedForReceive[key] === true;
    });
    
    if (selectedProducts.length === 0) {
      toast.error("Välj minst en produkt att ta emot");
      return;
    }
    
    // Build URL with selected item IDs as parameter for reliability
    // Format: order_id:ean pairs separated by comma
    const itemKeys = selectedProducts.map(p => `${p.order_id}:${p.ean}`).join(',');
    
    // Close dialog first
    setShowReceiveDialog(false);
    
    // Navigate with items in URL parameter
    const targetUrl = `/purchasing/receive?supplier=${receiveSupplier}&items=${encodeURIComponent(itemKeys)}`;
    navigate(targetUrl);
  };

  // Cheapest available priced response for a product (compared in SEK, shown in original currency)
  const cheapestResponse = (p) => {
    const avail = (p.inquiry_responses || []).filter(r => r.available && (r.price || 0) > 0);
    if (avail.length === 0) return null;
    return avail.reduce((best, r) =>
      convertToSEK(r.price, r.currency, exchangeRate) < convertToSEK(best.price, best.currency, exchangeRate) ? r : best
    );
  };

  // A line is "Prissatta" only when ALL asked suppliers have registered a response
  // (kan leverera / får ej tag / inget svar) AND at least one of them gave a price.
  const isPricedReady = (p) => {
    if (!['ready_for_inquiry', 'inquiry_sent'].includes(p.order_status)) return false;
    const inq = p.inquiry_suppliers || [];
    const responses = p.inquiry_responses || [];
    const allAnswered = inq.length > 0 && inq.every(sid => responses.some(r => r.supplier_id === sid));
    const hasPrice = responses.some(r => r.available && (r.price || 0) > 0);
    return allAnswered && hasPrice;
  };

  // Filter products
  const filteredProducts = allProducts.filter(p => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = searchQuery === "" || 
      p.artikelnummer?.toLowerCase().includes(searchLower) ||
      p.ean?.includes(searchQuery) ||
      p.produktnamn?.toLowerCase().includes(searchLower) ||
      p.reservations?.some(r => r.customer_order_number?.toLowerCase().includes(searchLower));
    
    const matchesSupplier = supplierFilter === "all" || p.supplier_id === supplierFilter;
    // "Beställda" column includes both ordered and in_transit (På väg is a marking, not a column)
    const matchesStatus = statusFilter === "all"
      ? true
      : statusFilter === "ordered"
        ? ['ordered', 'in_transit'].includes(p.order_status)
        : statusFilter === "priced"
          ? isPricedReady(p)
          : statusFilter === "inquiries"
            ? ['ready_for_inquiry', 'inquiry_sent'].includes(p.order_status)
            : p.order_status === statusFilter;
    
    // Under "Förfrågningar": selecting a supplier filters the list to that supplier's NOT-yet-sent lines
    const matchesInquirySupplier = statusFilter !== "inquiries" || !inquirySelectedSupplier
      || ((p.inquiry_suppliers || []).includes(inquirySelectedSupplier)
          && !(p.inquiry_sent_suppliers || []).includes(inquirySelectedSupplier));

    // Under "Prissatta": selecting a supplier filters to lines where that supplier is cheapest
    const matchesPricedSupplier = statusFilter !== "priced" || !inquirySelectedSupplier
      || (cheapestResponse(p)?.supplier_id === inquirySelectedSupplier);

    return matchesSearch && matchesSupplier && matchesStatus && matchesInquirySupplier && matchesPricedSupplier;
  });

  // Group lines by inquiry supplier for the per-supplier copy cards.
  // Built from the currently filtered lines so the cards match exactly what is shown
  // under the "Redo för förfrågan" filter (a partially-sent line stays ready_for_inquiry).
  const inquiryGroups = (() => {
    const searchLower = searchQuery.toLowerCase();
    const groups = {};
    // Built independent of the selected-supplier list filter so the dropdown keeps all suppliers
    for (const p of allProducts) {
      if (p.order_status !== 'ready_for_inquiry' && p.order_status !== 'inquiry_sent') continue;
      const matchesSearch = searchQuery === "" ||
        p.artikelnummer?.toLowerCase().includes(searchLower) ||
        p.ean?.includes(searchQuery) ||
        p.produktnamn?.toLowerCase().includes(searchLower);
      if (!matchesSearch) continue;
      for (const sid of (p.inquiry_suppliers || [])) {
        if (!groups[sid]) groups[sid] = { supplier_id: sid, lines: [] };
        // Only count/copy lines NOT yet sent to this supplier
        if (!(p.inquiry_sent_suppliers || []).includes(sid)) groups[sid].lines.push(p);
      }
    }
    return Object.values(groups)
      .filter(g => g.lines.length > 0)
      .sort((a, b) => supplierName(a.supplier_id).localeCompare(supplierName(b.supplier_id)));
  })();
  
  // Group priced lines by their CHEAPEST supplier for the per-supplier copy cards on "Prissatta".
  const pricedGroups = (() => {
    const searchLower = searchQuery.toLowerCase();
    const groups = {};
    for (const p of allProducts) {
      if (!isPricedReady(p)) continue;
      const c = cheapestResponse(p);
      if (!c) continue;
      const matchesSearch = searchQuery === "" ||
        p.artikelnummer?.toLowerCase().includes(searchLower) ||
        p.ean?.includes(searchQuery) ||
        p.produktnamn?.toLowerCase().includes(searchLower);
      if (!matchesSearch) continue;
      const sid = c.supplier_id;
      if (!groups[sid]) groups[sid] = { supplier_id: sid, lines: [] };
      groups[sid].lines.push(p);
    }
    return Object.values(groups)
      .filter(g => g.lines.length > 0)
      .sort((a, b) => supplierName(a.supplier_id).localeCompare(supplierName(b.supplier_id)));
  })();
  
  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / pageSize);
  const paginatedProducts = filteredProducts.slice(
    currentPage * pageSize,
    (currentPage + 1) * pageSize
  );
  
  // Reset to first page when filters change; clear per-supplier selection when switching tabs
  useEffect(() => {
    setCurrentPage(0);
  }, [searchQuery, supplierFilter, statusFilter]);

  useEffect(() => {
    setInquirySelectedSupplier("");
    setInquiryCopied(null);
    setInquiryCopyKeys([]);
  }, [statusFilter]);

  // Stats
  const stats = {
    total: filteredProducts.length,
    ordered: filteredProducts.filter(p => p.order_status === 'ordered').length,
    inTransit: filteredProducts.filter(p => p.order_status === 'in_transit').length
  };

  // Status column tabs (with counts) — replaces the old status dropdown filter
  const statusTabs = [
    { id: 'pending', label: 'Ska beställas', color: 'bg-red-600 hover:bg-red-700', count: allProducts.filter(p => p.order_status === 'pending').length },
    { id: 'inquiries', label: 'Förfrågningar', color: 'bg-indigo-600 hover:bg-indigo-700', count: allProducts.filter(p => ['ready_for_inquiry', 'inquiry_sent'].includes(p.order_status)).length },
    { id: 'priced', label: 'Prissatta', color: 'bg-emerald-600 hover:bg-emerald-700', count: allProducts.filter(isPricedReady).length },
    { id: 'ordered', label: 'Beställda', color: 'bg-blue-600 hover:bg-blue-700', count: allProducts.filter(p => ['ordered', 'in_transit'].includes(p.order_status)).length },
  ];

  const isLookup = statusFilter === 'lager' || statusFilter === 'prices';

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/purchasing">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <div>
                <h1 className="font-heading text-xl font-bold uppercase tracking-tight">
                  Beställda produkter
                </h1>
                <p className="text-sm text-muted-foreground">
                  {loading ? "Laddar..." : `${allProducts.length} produkter totalt`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                className="gap-2"
                onClick={() => setShowQuickOrderDialog(true)}
                data-testid="new-order-btn"
              >
                <Plus className="h-4 w-4" />
                Ny beställning
              </Button>
              <Button className="gap-2" onClick={openReceiveDialog}>
                <Truck className="h-4 w-4" />
                Ta emot leverans
              </Button>
              <Button
                className="gap-2 bg-amber-500 hover:bg-amber-600 text-black font-semibold"
                onClick={() => setShowSpecialOrder(true)}
                data-testid="special-order-btn"
              >
                <Zap className="h-4 w-4" />
                Specialorder
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 space-y-6">
        {/* View switcher: left = lookup views (pinned left), center = order status tabs */}
        <div className="relative flex flex-wrap justify-center items-center gap-3 min-h-[52px]" data-testid="order-status-tabs">
          <div className="md:absolute md:left-0 md:top-1/2 md:-translate-y-1/2 inline-flex flex-wrap gap-1 rounded-lg border border-border p-1 bg-card">
            {[
              { id: 'lager', label: 'Lager', color: 'bg-red-600 hover:bg-red-700' },
              { id: 'prices', label: 'Inköpspris', color: 'bg-emerald-700 hover:bg-emerald-800' },
            ].map(tab => {
              const isActive = statusFilter === tab.id;
              return (
                <Button
                  key={tab.id}
                  variant={isActive ? "default" : "ghost"}
                  className={`gap-2 ${isActive ? tab.color : ""}`}
                  onClick={() => setStatusFilter(tab.id)}
                  data-testid={`lookup-tab-${tab.id}`}
                >
                  {tab.label}
                </Button>
              );
            })}
          </div>
          <div className="inline-flex flex-wrap gap-1 rounded-lg border border-border p-1 bg-card">
            {statusTabs.map(tab => {
              const isActive = statusFilter === tab.id;
              return (
                <Button
                  key={tab.id}
                  variant={isActive ? "default" : "ghost"}
                  className={`gap-2 ${isActive ? tab.color : ""}`}
                  onClick={() => setStatusFilter(tab.id)}
                  data-testid={`status-tab-${tab.id}`}
                >
                  {tab.label}
                  <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-semibold ${isActive ? 'bg-white/25 text-white' : 'bg-muted text-muted-foreground'}`}>
                    {tab.count}
                  </span>
                </Button>
              );
            })}
          </div>
        </div>

        {/* Lookup views (Lager / Inköpspris) */}
        {isLookup && (
          <PurchasingLookup
            mode={statusFilter}
            suppliers={suppliers}
            exchangeRate={exchangeRate}
            search={searchQuery}
            onSearchChange={setSearchQuery}
            onOrder={(product) => {
              setQuickOrderPreselect({ ean: product.ean, artikelnummer: product.artikelnummer || '' });
              setShowQuickOrderDialog(true);
            }}
          />
        )}

        {!isLookup && (<>
        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Sök (artikelnummer, EAN, ordernummer)..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sub-filter + per-supplier inquiry panel — under "Förfrågningar" and "Prissatta" */}
        {(statusFilter === 'inquiries' || statusFilter === 'priced') && (() => {
          const isPriced = statusFilter === 'priced';
          const availableGroups = isPriced ? pricedGroups : inquiryGroups;
          const copyHandler = isPriced ? handleCopyPriced : handleCopyInquiry;
          if (availableGroups.length === 0) return null;
          const selected = availableGroups.find(g => g.supplier_id === inquirySelectedSupplier);
          const copied = selected && inquiryCopied === selected.supplier_id;
          const groupKeys = selected ? selected.lines.map(inquiryLineKey) : [];
          const checkedCount = groupKeys.filter(k => inquiryCopyKeys.includes(k)).length;
          const selectSupplier = (v) => {
            const grp = availableGroups.find(g => g.supplier_id === v);
            setInquirySelectedSupplier(v);
            setInquiryCopied(null);
            setInquiryCopyKeys(grp ? grp.lines.map(inquiryLineKey) : []);
          };
          return (
            <Card className="bg-cyan-500/5 border-cyan-500/20" data-testid="inquiry-panel">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Send className="h-4 w-4 text-cyan-600" />
                  <p className="font-medium">{isPriced ? 'Beställ per leverantör (billigaste)' : 'Förfrågningar per leverantör'}</p>
                  <span className="text-sm text-muted-foreground">– {isPriced ? 'produkter grupperas på billigaste leverantören – bocka i, kopiera listan med priser och bekräfta mejlet' : 'välj leverantör, bocka i produkterna, kopiera listan och bekräfta att mejlet skickats'}</span>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  <Select value={selected?.supplier_id || ""} onValueChange={selectSupplier}>
                    <SelectTrigger className="sm:w-72" data-testid="inquiry-supplier-dropdown">
                      <Building2 className="h-4 w-4 mr-2 text-cyan-600" />
                      <SelectValue placeholder="Välj leverantör att skicka till..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableGroups.map(g => (
                        <SelectItem key={g.supplier_id} value={g.supplier_id}>
                          {supplierName(g.supplier_id)} ({g.lines.length})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selected && (copied ? (
                    <Button
                      className="gap-1 bg-green-600 hover:bg-green-700 text-white shrink-0"
                      data-testid={`confirm-sent-${selected.supplier_id}`}
                      onClick={() => handleConfirmSent(selected)}
                    >
                      <CheckCircle className="h-3.5 w-3.5" />
                      Skickat mejl
                    </Button>
                  ) : (
                    <Button
                      className="gap-1 shrink-0"
                      disabled={checkedCount === 0}
                      data-testid={`copy-inquiry-${selected.supplier_id}`}
                      onClick={() => copyHandler(selected)}
                    >
                      <Copy className="h-3.5 w-3.5" />
                      Kopiera lista ({checkedCount})
                    </Button>
                  ))}
                  {selected && (
                    <Button
                      variant="ghost"
                      className="gap-1 shrink-0 text-muted-foreground"
                      onClick={() => { setInquirySelectedSupplier(""); setInquiryCopied(null); setInquiryCopyKeys([]); }}
                      data-testid="clear-inquiry-filter"
                    >
                      <X className="h-3.5 w-3.5" />
                      Rensa filter
                    </Button>
                  )}
                </div>
                {selected && !copied && (
                  <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-cyan-500/10">
                    <span className="text-xs text-muted-foreground">
                      {checkedCount} av {groupKeys.length} produkter markerade
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1 h-7"
                      onClick={() => setInquiryCopyKeys(groupKeys)}
                      data-testid="inquiry-select-all"
                    >
                      <CheckSquare className="h-3.5 w-3.5" />
                      Markera alla
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1 h-7"
                      onClick={() => setInquiryCopyKeys([])}
                      data-testid="inquiry-deselect-all"
                    >
                      <Square className="h-3.5 w-3.5" />
                      Avmarkera alla
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {/* Products List */}
        {loading ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="h-12 w-12 mx-auto mb-4 text-primary animate-spin" />
              <p className="text-muted-foreground">Laddar beställningar...</p>
            </CardContent>
          </Card>
        ) : filteredProducts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-heading text-lg mb-2">Inga beställningar</h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery || supplierFilter !== "all" || statusFilter !== "all" 
                  ? "Inga produkter matchar filtret" 
                  : "Det finns inga aktiva beställningar"}
              </p>
              <Button onClick={() => {
                // Pre-fill the search query if user had searched for something
                if (searchQuery) {
                  setQuickOrderPreselect({ searchQuery: searchQuery });
                }
                setShowQuickOrderDialog(true);
              }}>
                <Plus className="h-4 w-4 mr-2" />
                Skapa beställning
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {paginatedProducts.map((product, idx) => (
              <Card key={`${product.order_id}-${product.ean}-${idx}`} className="hover:bg-accent/50 transition-colors">
                <CardContent className="py-3 px-4">
                  <div className="flex items-center justify-between gap-4">
                    {statusFilter === 'inquiries' && inquirySelectedSupplier
                      && (product.inquiry_suppliers || []).includes(inquirySelectedSupplier)
                      && !(product.inquiry_sent_suppliers || []).includes(inquirySelectedSupplier)
                      && inquiryCopied !== inquirySelectedSupplier && (
                      <Checkbox
                        checked={inquiryCopyKeys.includes(inquiryLineKey(product))}
                        onCheckedChange={(v) => {
                          const key = inquiryLineKey(product);
                          setInquiryCopyKeys(prev => v ? [...new Set([...prev, key])] : prev.filter(k => k !== key));
                        }}
                        className="shrink-0"
                        data-testid={`inquiry-copy-check-${product.ean}`}
                      />
                    )}
                    {/* Left: Grid layout for consistent alignment */}
                    <div className="grid grid-cols-[140px_80px_120px_90px_60px_1fr] items-center gap-3 flex-1">
                      <div>
                        <Link to={`/inventory/${product.ean}`} className="hover:underline">
                          <span className="font-mono font-bold text-primary text-sm">{product.artikelnummer}</span>
                        </Link>
                        <p className="text-xs text-muted-foreground font-mono">{product.ean}</p>
                      </div>
                      
                      {/* Purchase price */}
                      <div className="text-sm font-medium">
                        {product.unit_price > 0 ? (
                          <>
                            <span className="text-foreground">
                              {(product.currency === 'EUR' 
                                ? Math.round(product.unit_price * exchangeRate) 
                                : Math.round(product.unit_price)
                              ).toLocaleString('sv-SE')}
                            </span>
                            <span className="text-muted-foreground ml-1">kr</span>
                            {product.currency === 'EUR' && (
                              <span className="block text-[10px] text-muted-foreground">
                                {Math.round(product.unit_price).toLocaleString('sv-SE')} €
                              </span>
                            )}
                          </>
                        ) : <span className="text-muted-foreground">—</span>}
                      </div>
                      
                      {product.supplier_name ? (
                        <div className="flex items-center gap-1 text-amber-600 bg-amber-500/10 px-2 py-0.5 rounded text-sm truncate" title={product.supplier_name}>
                          <Building2 className="h-3 w-3 shrink-0" />
                          <span className="font-medium truncate">{product.supplier_name}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-muted-foreground bg-muted px-2 py-0.5 rounded text-sm truncate" title="Ingen leverantör vald än">
                          <Building2 className="h-3 w-3 shrink-0" />
                          <span className="truncate">Ingen leverantör än</span>
                        </div>
                      )}
                      
                      {/* Status timestamp */}
                      <span className="text-xs text-muted-foreground truncate" title={new Date(product.status_updated_at).toLocaleString("sv-SE")}>
                        {formatRelativeTime(product.status_updated_at)}
                      </span>
                      
                      {/* Week — only relevant for ordered/in_transit ("Beställda") */}
                      {['ordered', 'in_transit'].includes(product.order_status) ? (
                        <div>
                          {product.expected_week ? (
                            <button
                              onClick={() => openEditWeekDialog(product)}
                              className={`text-xs px-2 py-0.5 rounded transition-colors ${
                                product.expected_week < currentWeek
                                  ? "bg-red-500/10 text-red-500 hover:bg-red-500/20"
                                  : "bg-green-500/10 text-green-600 hover:bg-green-500/20"
                              }`}
                            >
                              v.{product.expected_week < currentWeek ? currentWeek : product.expected_week}
                              {product.expected_week < currentWeek && " (försenad)"}
                            </button>
                          ) : (
                            <button
                              onClick={() => openEditWeekDialog(product)}
                              className="text-xs text-muted-foreground hover:text-foreground"
                            >
                              +vecka
                            </button>
                          )}
                        </div>
                      ) : (
                        <div />
                      )}
                      
                      {/* Reservations */}
                      <div className="flex items-center gap-1 flex-wrap">
                        {product.order_status === 'pending' && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1 h-7 border-indigo-500/40 text-indigo-600 hover:bg-indigo-500/10 mr-1"
                            onClick={() => openInquiryDialog(product)}
                            data-testid={`inquiry-suppliers-${product.ean}`}
                            title="Välj leverantörer för förfrågan"
                          >
                            <Building2 className="h-3.5 w-3.5" />
                            {product.inquiry_suppliers?.length > 0 ? `Välj leverantörer (${product.inquiry_suppliers.length})` : "Välj leverantörer"}
                          </Button>
                        )}
                        {product.order_status === 'inquiry_sent' &&
                         (product.inquiry_responses || []).some(r => r.available && r.price) && (
                          <Button
                            size="sm"
                            className="gap-1 h-7 bg-green-600 hover:bg-green-700 text-white mr-1"
                            onClick={() => openChooseDialog(product)}
                            data-testid={`choose-supplier-${product.ean}`}
                            title="Välj leverantör och beställ"
                          >
                            <CheckCircle className="h-3.5 w-3.5" />
                            Välj leverantör &amp; beställ
                          </Button>
                        )}
                        {product.reservations.map(res => (
                          <div 
                            key={res.id}
                            className="flex items-center gap-1 px-2 py-0.5 bg-orange-500/10 rounded text-sm"
                          >
                            <Bookmark className="h-3 w-3 text-orange-500" />
                            <span className="font-medium">{res.customer_order_number}</span>
                            <span className="text-muted-foreground text-xs">({res.quantity})</span>
                            <button
                              className="ml-0.5 text-muted-foreground hover:text-destructive transition-colors"
                              onClick={() => handleDeleteReservation(res.id)}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Right: Quantities & Actions */}
                    <div className="flex items-center gap-3">
                      <div className="text-center min-w-[50px]">
                        <p className={`text-xl font-bold ${statusColor(product.order_status)}`}>{product.pending}</p>
                        <p className={`text-[10px] ${statusColor(product.order_status)}`}>
                          {statusText(product.order_status)}
                        </p>
                      </div>
                      {product.reserved > 0 && (
                        <div className="text-center min-w-[50px]">
                          <p className="text-xl font-bold text-orange-500">{product.reserved}</p>
                          <p className="text-[10px] text-orange-500">Reserv.</p>
                        </div>
                      )}
                      <div className="text-center min-w-[50px]">
                        <p className={`text-xl font-bold ${product.unreserved > 0 ? 'text-green-500' : 'text-muted-foreground'}`}>
                          {product.unreserved}
                        </p>
                        <p className="text-[10px] text-muted-foreground">Tillg.</p>
                      </div>
                      
                      {/* Note indicator - copyable text */}
                      {product.note && (
                        <div
                          className="flex items-center gap-1 px-2 py-1 bg-purple-500/10 rounded text-sm cursor-text select-all"
                          title={`Dubbelklicka för att markera: ${product.note}`}
                        >
                          <StickyNote className="h-3 w-3 text-purple-500 flex-shrink-0" />
                          <span className="text-purple-500 text-xs max-w-[150px] truncate select-all">{product.note}</span>
                        </div>
                      )}
                      
                      {/* Actions */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`row-actions-${product.ean}`}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {['pending', 'ready_for_inquiry', 'inquiry_sent'].includes(product.order_status) && (
                            <>
                              <DropdownMenuItem onClick={() => openInquiryDialog(product)} data-testid={`menu-inquiry-suppliers-${product.ean}`}>
                                <Building2 className="h-4 w-4 mr-2 text-indigo-500" />
                                Välj leverantörer
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                            </>
                          )}
                          {product.unreserved > 0 && (
                            <DropdownMenuItem onClick={() => openReservationDialog(product)}>
                              <Bookmark className="h-4 w-4 mr-2 text-orange-500" />
                              Reservera ({product.unreserved} st tillgängliga)
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => openNoteDialog(product)}>
                            <StickyNote className="h-4 w-4 mr-2 text-purple-500" />
                            {product.note ? "Redigera notering" : "Skapa notering"}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEditQuantityDialog(product)}>
                            <Pencil className="h-4 w-4 mr-2 text-blue-500" />
                            Redigera antal ({product.quantity} st)
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {['ordered', 'in_transit'].includes(product.order_status) && (
                            <>
                              <DropdownMenuItem 
                                onClick={() => handleChangeOrderStatus(product.order_id, 'ordered')}
                                disabled={product.order_status === 'ordered'}
                              >
                                <Clock className="h-4 w-4 mr-2 text-blue-500" />
                                Markera som beställd
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleChangeOrderStatus(product.order_id, 'in_transit')}
                                disabled={product.order_status === 'in_transit'}
                              >
                                <Truck className="h-4 w-4 mr-2 text-emerald-500" />
                                Markera som på väg
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                            </>
                          )}
                          <DropdownMenuItem 
                            onClick={() => handleRemoveProduct(product)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Ta bort från order
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {/* Inquiry chips: selected suppliers + responses (hidden once ordered) */}
                  {(product.inquiry_suppliers?.length > 0) && !['ordered', 'in_transit'].includes(product.order_status) && (
                    <div className="flex flex-wrap items-center gap-1.5 mt-2 pt-2 border-t border-border/50">
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground mr-1">Förfrågan:</span>
                      {product.inquiry_suppliers.map(sid => {
                        // Response info (price / får ej tag) only surfaces under "Förfrågan skickad"
                        const resp = product.order_status === 'inquiry_sent'
                          ? (product.inquiry_responses || []).find(r => r.supplier_id === sid)
                          : null;
                        const sent = (product.inquiry_sent_suppliers || []).includes(sid);
                        const caseNo = (product.inquiry_case_numbers || {})[sid];
                        let cls = "bg-muted text-muted-foreground";
                        let label = supplierName(sid);
                        if (resp && resp.available === false) {
                          cls = "bg-red-500/10 text-red-600 line-through";
                          label = `${supplierName(sid)} – får ej tag`;
                        } else if (resp && resp.available === true) {
                          cls = "bg-green-500/10 text-green-600";
                          label = `${supplierName(sid)}${resp.price ? ` – ${dualPriceText(resp.price, resp.currency || 'SEK', exchangeRate)}` : ' – tillgänglig'}`;
                        } else if (resp && (resp.available === null || resp.available === undefined)) {
                          cls = "bg-muted text-muted-foreground";
                          label = `${supplierName(sid)} – inget svar`;
                        } else if (sent) {
                          cls = "bg-cyan-500/10 text-cyan-700";
                          label = `${supplierName(sid)} – skickad`;
                        }
                        if (caseNo) label += ` · #${caseNo}`;
                        return (
                          <button
                            key={sid}
                            className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs ${cls}`}
                            data-testid={`inquiry-chip-${product.ean}-${sid}`}
                            onClick={() => openResponseDialog(product, sid)}
                            title="Klicka för att registrera/ändra svar"
                          >
                            {resp && resp.available === false
                              ? <X className="h-3 w-3" />
                              : resp && resp.available === true
                                ? <CheckCircle className="h-3 w-3" />
                                : resp && (resp.available === null || resp.available === undefined)
                                  ? <HelpCircle className="h-3 w-3" />
                                  : <Send className="h-3 w-3" />}
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 text-sm text-muted-foreground">
            <span>
              Visar {currentPage * pageSize + 1}-{Math.min((currentPage + 1) * pageSize, filteredProducts.length)} av {filteredProducts.length} produkter
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                disabled={currentPage === 0}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="px-2">
                Sida {currentPage + 1} av {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={currentPage >= totalPages - 1}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
        </>)}
      </main>

      {/* Reservation Dialog */}
      <Dialog open={showReservationDialog} onOpenChange={setShowReservationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bookmark className="h-5 w-5 text-orange-500" />
              Reservera produkt
            </DialogTitle>
            <DialogDescription>
              Reservera {selectedProduct?.artikelnummer} för en kundorder
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="font-mono font-bold">{selectedProduct?.artikelnummer}</p>
              <p className="text-sm text-muted-foreground">
                {selectedProduct?.unreserved} st tillgängliga för reservation
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Ordernummer / Referens *</Label>
              <Input
                placeholder="T.ex. KO-12345"
                value={reservationForm.customer_order_number}
                onChange={(e) => setReservationForm(prev => ({ ...prev, customer_order_number: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Antal att reservera *</Label>
              <Input
                type="number"
                min={1}
                max={selectedProduct?.unreserved || 1}
                value={reservationForm.quantity}
                onChange={(e) => setReservationForm(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
              />
              <p className="text-xs text-muted-foreground">
                Max {selectedProduct?.unreserved} st
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReservationDialog(false)}>
              Avbryt
            </Button>
            <Button 
              onClick={handleAddReservation} 
              disabled={saving || !reservationForm.customer_order_number || reservationForm.quantity < 1}
            >
              {saving ? "Sparar..." : "Reservera"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stock available -> ask whether to convert reservation to a booking */}
      <Dialog open={showConvertDialog} onOpenChange={(o) => { if (!o) { setShowConvertDialog(false); setConvertPrompt(null); } }}>
        <DialogContent className="max-w-md" data-testid="convert-reservation-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-green-600" />
              Produkten finns på lager
            </DialogTitle>
            <DialogDescription>
              {convertPrompt?.artikelnummer} finns på lager ({convertPrompt?.available} st tillgängligt).
              Vill du göra reservationen till en bokning istället?
            </DialogDescription>
          </DialogHeader>

          <div className="p-3 bg-muted rounded-lg text-sm">
            <p><span className="text-muted-foreground">Kundorder:</span> <span className="font-medium">{convertPrompt?.customerOrderNumber}</span></p>
            <p><span className="text-muted-foreground">Antal:</span> <span className="font-medium">{convertPrompt?.quantity} st</span></p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowConvertDialog(false); setConvertPrompt(null); }} data-testid="keep-reservation-btn">
              Behåll som reservation
            </Button>
            <Button onClick={handleConvertReservation} disabled={saving} className="bg-green-600 hover:bg-green-700" data-testid="convert-to-booking-btn">
              {saving ? "Omvandlar..." : "Gör till bokning"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Edit Expected Week Dialog */}
      <Dialog open={showEditWeekDialog} onOpenChange={setShowEditWeekDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Ändra förväntad leveransvecka</DialogTitle>
            <DialogDescription>
              {editWeekProduct?.artikelnummer} från {editWeekProduct?.supplier_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Förväntad leveransvecka</Label>
              <Select value={editWeekValue} onValueChange={setEditWeekValue}>
                <SelectTrigger>
                  <SelectValue placeholder="Välj vecka..." />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {Array.from({ length: 52 }, (_, i) => {
                    const weekNum = i + 1;
                    const isPastWeek = weekNum < currentWeek;
                    const isCurrentWeekNum = weekNum === currentWeek;
                    
                    if (isPastWeek) return null; // Hide past weeks
                    
                    return (
                      <SelectItem key={weekNum} value={weekNum.toString()}>
                        Vecka {weekNum} {isCurrentWeekNum && "(denna vecka)"}
                      </SelectItem>
                    );
                  }).filter(Boolean)}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditWeekDialog(false)}>
              Avbryt
            </Button>
            <Button onClick={handleUpdateExpectedWeek} disabled={saving}>
              {saving ? "Sparar..." : "Spara"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Quantity Dialog */}
      <Dialog open={showEditQuantityDialog} onOpenChange={setShowEditQuantityDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-blue-500" />
              Redigera antal
            </DialogTitle>
            <DialogDescription>
              {editQuantityProduct?.artikelnummer} - {editQuantityProduct?.supplier_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="font-mono font-bold">{editQuantityProduct?.artikelnummer}</p>
              <p className="text-sm text-muted-foreground">
                {editQuantityProduct?.received > 0 
                  ? `${editQuantityProduct.received} st redan mottagna av ${editQuantityProduct.quantity}`
                  : `Beställt: ${editQuantityProduct?.quantity} st`
                }
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Nytt antal</Label>
              <Input
                type="number"
                min={editQuantityProduct?.received || 1}
                value={editQuantityValue}
                onChange={(e) => setEditQuantityValue(parseInt(e.target.value) || 1)}
              />
              {editQuantityProduct?.received > 0 && (
                <p className="text-xs text-muted-foreground">
                  Minst {editQuantityProduct.received} st (redan mottagna)
                </p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditQuantityDialog(false)}>
              Avbryt
            </Button>
            <Button 
              onClick={handleUpdateQuantity} 
              disabled={saving || editQuantityValue < 1}
            >
              {saving ? "Sparar..." : "Spara"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="h-5 w-5 text-purple-500" />
              {noteProduct?.note ? "Redigera notering" : "Skapa notering"}
            </DialogTitle>
            <DialogDescription>
              {noteProduct?.artikelnummer} - {noteProduct?.supplier_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Notering</Label>
              <Textarea
                placeholder="Skriv en notering..."
                value={noteValue}
                onChange={(e) => setNoteValue(e.target.value)}
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                Noteringen visas på produktraden i listan
              </p>
            </div>
          </div>
          
          <DialogFooter>
            {noteProduct?.note && (
              <Button 
                variant="ghost" 
                className="mr-auto text-destructive"
                onClick={() => {
                  setNoteValue("");
                  handleSaveNote();
                }}
                disabled={saving}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Ta bort
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowNoteDialog(false)}>
              Avbryt
            </Button>
            <Button onClick={handleSaveNote} disabled={saving}>
              {saving ? "Sparar..." : "Spara"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Optional ärendenummer after "Skickat mejl" */}
      <Dialog open={showSentCaseDialog} onOpenChange={setShowSentCaseDialog}>
        <DialogContent className="max-w-md" data-testid="sent-case-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-cyan-600" />
              Ärendenummer (valfritt)
            </DialogTitle>
            <DialogDescription>
              Förfrågan till {sentCaseGroup ? supplierName(sentCaseGroup.supplier_id) : ""}. Ange ett ärendenummer för mejlet om du har ett – det visas sedan vid leverantören.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="sent-case-input">Ärendenummer</Label>
            <Input
              id="sent-case-input"
              autoFocus
              placeholder="t.ex. ABC-12345"
              value={sentCaseNumber}
              onChange={(e) => setSentCaseNumber(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); doConfirmSent(); } }}
              data-testid="sent-case-input"
            />
            <p className="text-xs text-muted-foreground">Lämna tomt om du inte har något ärendenummer.</p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => doConfirmSent("")} data-testid="sent-case-skip">
              Hoppa över
            </Button>
            <Button onClick={() => doConfirmSent()} data-testid="sent-case-save">
              Markera skickad
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Receive Delivery Dialog */}
      <Dialog open={showReceiveDialog} onOpenChange={setShowReceiveDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-green-500" />
              Ta emot leverans
            </DialogTitle>
            <DialogDescription>
              Välj leverantör och markera produkterna du vill ta emot
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
            {/* Supplier selection */}
            <div className="space-y-2">
              <Label>Välj leverantör</Label>
              <Select value={receiveSupplier} onValueChange={handleSupplierSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Välj leverantör..." />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.filter(s => allProducts.some(p => 
                    p.supplier_id === s.id && 
                    (p.order_status === "ordered" || p.order_status === "in_transit")
                  )).map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Status filter buttons */}
            {receiveSupplier && receiveProducts.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Visa:</span>
                <Button
                  variant={receiveStatusFilter === "all" ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleFilterChange("all")}
                >
                  Alla ({receiveProducts.length})
                </Button>
                <Button
                  variant={receiveStatusFilter === "ordered" ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleFilterChange("ordered")}
                  className="gap-1"
                >
                  <Clock className="h-3 w-3" />
                  Beställd ({receiveProducts.filter(p => p.order_status === "ordered").length})
                </Button>
                <Button
                  variant={receiveStatusFilter === "in_transit" ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleFilterChange("in_transit")}
                  className="gap-1"
                >
                  <Truck className="h-3 w-3" />
                  På väg ({receiveProducts.filter(p => p.order_status === "in_transit").length})
                </Button>
              </div>
            )}
            
            {/* Products list */}
            {receiveSupplier && (
              <div className="flex-1 overflow-auto border rounded-md">
                {filteredReceiveProducts.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    {receiveProducts.length === 0 
                      ? "Inga produkter att ta emot från denna leverantör"
                      : "Inga produkter matchar valt filter"
                    }
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredReceiveProducts.map(product => {
                      const key = `${product.order_id}-${product.ean}`;
                      const isSelected = selectedForReceive[key];
                      return (
                        <div 
                          key={key}
                          className={`p-3 flex items-center gap-3 cursor-pointer hover:bg-muted/50 ${isSelected ? 'bg-green-500/5' : ''}`}
                          onClick={() => toggleProductSelection(product)}
                        >
                          <input 
                            type="checkbox" 
                            checked={isSelected || false}
                            onChange={() => {}}
                            className="h-4 w-4 rounded border-gray-300"
                          />
                          <div className="flex-1">
                            <p className="font-mono font-bold">{product.artikelnummer}</p>
                            <p className="text-xs text-muted-foreground">{product.ean}</p>
                          </div>
                          {getStatusBadge(product.order_status)}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowReceiveDialog(false)}>
              Avbryt
            </Button>
            <Button 
              onClick={handleProceedToReceive} 
              disabled={!receiveSupplier || Object.values(selectedForReceive).filter(Boolean).length === 0}
              className="gap-2"
            >
              <PackageCheck className="h-4 w-4" />
              Ta emot ({Object.values(selectedForReceive).filter(Boolean).length} produkter)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inquiry: select suppliers to request from */}
      <Dialog open={showInquiryDialog} onOpenChange={setShowInquiryDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-indigo-500" />
              Välj leverantörer för förfrågan
            </DialogTitle>
            <DialogDescription>
              {inquiryProduct?.artikelnummer} – välj vilka leverantörer du vill fråga om pris och tillgänglighet
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-72 overflow-auto space-y-1">
            {(() => {
              const usage = inquiryUsageMap();
              const sorted = [...suppliers].sort((a, b) => {
                const ap = inquiryPricedIds.includes(a.id) ? 1 : 0;
                const bp = inquiryPricedIds.includes(b.id) ? 1 : 0;
                if (ap !== bp) return bp - ap;
                const au = usage[a.id] || 0, bu = usage[b.id] || 0;
                if (au !== bu) return bu - au;
                return a.company_name.localeCompare(b.company_name);
              });
              return sorted.map(s => {
                const selected = inquirySelected.includes(s.id);
                const priceInfo = inquiryPriceMap[s.id];
                const priceSek = priceInfo
                  ? (priceInfo.currency === 'EUR' ? Math.round(priceInfo.price * exchangeRate) : Math.round(priceInfo.price))
                  : null;
                return (
                  <button
                    key={s.id}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-md border transition-colors ${selected ? 'border-indigo-500 bg-indigo-500/10' : 'border-border hover:bg-muted'}`}
                    onClick={() => toggleInquirySupplier(s.id)}
                    data-testid={`inquiry-supplier-option-${s.id}`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="font-medium">{s.company_name}</span>
                      {priceInfo && (
                        <span className="text-green-600 text-sm font-semibold">
                          {priceSek.toLocaleString('sv-SE')} kr
                          {priceInfo.currency === 'EUR' && (
                            <span className="text-xs font-normal text-green-600/70 ml-1">({priceInfo.price} €)</span>
                          )}
                        </span>
                      )}
                      {!priceInfo && inquiryUnavailableIds.includes(s.id) && (
                        <Badge variant="outline" className="text-red-600 border-red-500/50 bg-red-500/5 text-xs gap-1">
                          <X className="h-3 w-3" />Otillgänglig
                        </Badge>
                      )}
                    </span>
                    {selected && <CheckCircle className="h-4 w-4 text-indigo-500" />}
                  </button>
                );
              });
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInquiryDialog(false)}>Avbryt</Button>
            <Button onClick={handleSaveInquirySuppliers} disabled={saving} data-testid="save-inquiry-suppliers">
              {saving ? "Sparar..." : `Spara (${inquirySelected.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inquiry: register a supplier response */}
      <Dialog open={showResponseDialog} onOpenChange={setShowResponseDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-cyan-600" />
              Registrera leverantörssvar
            </DialogTitle>
            <DialogDescription>
              {responseProduct?.artikelnummer} – vad svarade leverantören?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Leverantör</Label>
              <Select value={responseForm.supplier_id} onValueChange={(v) => {
                const tax = responseForm.chemical_tax ? parseFloat(responseForm.chemical_tax) : 0;
                setResponseForm(responseFormForSupplier(responseProduct, v, tax, responsePriceMap));
              }}>
                <SelectTrigger data-testid="response-supplier"><SelectValue placeholder="Välj leverantör..." /></SelectTrigger>
                <SelectContent>
                  {(responseProduct?.inquiry_suppliers?.length ? responseProduct.inquiry_suppliers : suppliers.map(s => s.id)).map(sid => (
                    <SelectItem key={sid} value={sid}>{supplierName(sid)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={responseForm.available === true ? "default" : "outline"}
                className="flex-1 gap-1"
                onClick={() => setResponseForm(prev => ({ ...prev, available: true }))}
                data-testid="response-available"
              >
                <CheckCircle className="h-4 w-4" /> Kan leverera
              </Button>
              <Button
                type="button"
                variant={responseForm.available === false ? "destructive" : "outline"}
                className="flex-1 gap-1"
                onClick={() => setResponseForm(prev => ({ ...prev, available: false }))}
                data-testid="response-unavailable"
              >
                <X className="h-4 w-4" /> Får ej tag
              </Button>
              <Button
                type="button"
                variant={(responseForm.available === null || responseForm.available === undefined) ? "secondary" : "outline"}
                className="flex-1 gap-1"
                onClick={() => setResponseForm(prev => ({ ...prev, available: null }))}
                data-testid="response-noresponse"
              >
                <HelpCircle className="h-4 w-4" /> Inget svar
              </Button>
            </div>
            {responseForm.available === true && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Inköpspris</Label>
                    <Input type="number" step="0.01" min="0" placeholder="0" value={responseForm.price}
                      onChange={(e) => setResponseForm(prev => ({ ...prev, price: e.target.value }))} data-testid="response-price" />
                  </div>
                  <div className="space-y-2">
                    <Label>Valuta</Label>
                    <Select value={responseForm.currency} onValueChange={(v) => setResponseForm(prev => ({ ...prev, currency: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SEK">SEK</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="DKK">DKK</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Kem. skatt (SEK per st)</Label>
                  <Input type="number" step="0.01" min="0" placeholder="0" value={responseForm.chemical_tax}
                    onChange={(e) => setResponseForm(prev => ({ ...prev, chemical_tax: e.target.value }))} data-testid="response-chemtax" />
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label>Notering (valfritt)</Label>
              <Textarea rows={2} placeholder={responseForm.available ? "T.ex. leveransvecka" : "T.ex. utgått ur sortiment"}
                value={responseForm.note} onChange={(e) => setResponseForm(prev => ({ ...prev, note: e.target.value }))} />
            </div>
            {responseForm.available === false && (
              <p className="text-xs text-muted-foreground">
                Detta loggas i prislistan och markerar leverantören som &quot;Otillgänglig&quot; för produkten.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResponseDialog(false)}>Avbryt</Button>
            <Button onClick={handleSaveResponse} disabled={saving} data-testid="save-response">
              {saving ? "Sparar..." : "Spara svar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inquiry: choose final supplier + actual price */}
      <Dialog open={showChooseDialog} onOpenChange={setShowChooseDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Välj leverantör &amp; beställ
            </DialogTitle>
            <DialogDescription>
              {chooseProduct?.artikelnummer} – ange var den beställs och faktiskt pris
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {(chooseProduct?.inquiry_responses?.length > 0) && (
              <div className="bg-muted/40 rounded-lg p-2 space-y-1 text-sm">
                <p className="text-xs font-medium text-muted-foreground uppercase">Svar</p>
                {chooseProduct.inquiry_responses.map(r => (
                  <div key={r.supplier_id} className="flex items-center justify-between">
                    <span>{supplierName(r.supplier_id)}</span>
                    <span className={r.available === false ? "text-red-500" : r.available === true ? "text-green-600" : "text-muted-foreground"}>
                      {r.available === false ? "Får ej tag" : r.available === true ? (r.price ? dualPriceText(r.price, r.currency || 'SEK', exchangeRate) : "Tillgänglig") : "Inget svar"}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-2">
              <Label>Beställs från *</Label>
              {(() => {
                const priced = (chooseProduct?.inquiry_responses || []).filter(r => r.available && r.price);
                if (priced.length === 0) {
                  return (
                    <p className="text-sm text-muted-foreground border rounded-md p-3">
                      Inga leverantörer har lämnat pris ännu. Registrera ett leverantörssvar med pris först.
                    </p>
                  );
                }
                return (
                  <Select value={chooseForm.supplier_id} onValueChange={(v) => {
                    const r = priced.find(x => x.supplier_id === v);
                    setChooseForm(prev => ({
                      ...prev,
                      supplier_id: v,
                      unit_price: r?.price ? String(r.price) : prev.unit_price,
                      currency: r?.currency || prev.currency,
                      chemical_tax: r?.chemical_tax != null ? String(r.chemical_tax) : prev.chemical_tax
                    }));
                  }}>
                    <SelectTrigger data-testid="choose-supplier-select"><SelectValue placeholder="Välj leverantör..." /></SelectTrigger>
                    <SelectContent>
                      {priced
                        .slice()
                        .sort((a, b) => (a.price || 0) - (b.price || 0))
                        .map(r => (
                          <SelectItem key={r.supplier_id} value={r.supplier_id}>
                            {supplierName(r.supplier_id)} – {dualPriceText(r.price, r.currency || 'SEK', exchangeRate)}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                );
              })()}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Faktiskt pris *</Label>
                <Input type="number" step="0.01" min="0" placeholder="0" value={chooseForm.unit_price}
                  onChange={(e) => setChooseForm(prev => ({ ...prev, unit_price: e.target.value }))} data-testid="choose-price" />
              </div>
              <div className="space-y-2">
                <Label>Valuta</Label>
                <Select value={chooseForm.currency} onValueChange={(v) => setChooseForm(prev => ({ ...prev, currency: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SEK">SEK</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="DKK">DKK</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Kem. skatt (SEK per st)</Label>
              <Input type="number" step="0.01" min="0" placeholder="0" value={chooseForm.chemical_tax}
                onChange={(e) => setChooseForm(prev => ({ ...prev, chemical_tax: e.target.value }))} data-testid="choose-chemtax" />
            </div>
            <div className="space-y-2">
              <Label>Förväntad leveransvecka</Label>
              <Select value={chooseForm.expected_week} onValueChange={(v) => setChooseForm(prev => ({ ...prev, expected_week: v }))}>
                <SelectTrigger data-testid="choose-week"><SelectValue placeholder="Välj vecka..." /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {Array.from({ length: 12 }, (_, i) => {
                    let weekNum = currentWeek + i;
                    if (weekNum > 52) weekNum -= 52;
                    return (
                      <SelectItem key={i} value={weekNum.toString()}>
                        Vecka {weekNum} {i === 0 && "(denna vecka)"}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground">
              Priset sparas i prislistan och raden markeras som &quot;Beställd&quot;.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChooseDialog(false)}>Avbryt</Button>
            <Button onClick={handleChooseSupplier} disabled={saving || !chooseForm.supplier_id || !chooseForm.unit_price} className="gap-2" data-testid="save-choose-supplier">
              <CheckCircle className="h-4 w-4" />
              {saving ? "Sparar..." : "Beställ"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Order Dialog - Shared Component */}
      <QuickOrderDialog
        open={showQuickOrderDialog}
        onOpenChange={(open) => {
          setShowQuickOrderDialog(open);
          if (!open) setQuickOrderPreselect(null);
        }}
        suppliers={suppliers}
        exchangeRate={exchangeRate}
        preselectedProduct={quickOrderPreselect}
        onOrderCreated={() => {
          setSearchQuery("");
          setQuickOrderPreselect(null);
          fetchData();
        }}
      />

      <SpecialOrderDialog
        open={showSpecialOrder}
        onOpenChange={setShowSpecialOrder}
        suppliers={suppliers}
        onCreated={() => fetchData()}
      />
    </div>
  );
}
